import { redirectUrlError, redirectDelayError } from '../utils/redirectUrl'
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ImageIcon, X, Mail } from 'lucide-react'
import { buildApiUrl } from '../config'
import { normalizeHandle } from '../auth'
import {
  getAccessToken,
  isAccountLoggedIn,
  accountSignUp,
  accountConfirmSignUp,
  accountSignIn,
  accountResendConfirmationCode,
} from '../accountAuth'
import { Button, Card, Input, Badge, Alert } from './ui'
import { CodeAppearance, defaultAppearance, messageTemplates, themeStyles } from '../appearance'
import { isNativeApp, takeProfilePhoto } from '../native'

type Step = 1 | 2
type AuthMode = 'signup' | 'login'
type AuthSubStep = 'creds' | 'verify'

const SetupNew = () => {
  const navigate = useNavigate()

  // Step 1: code form
  const [step, setStep] = useState<Step>(1)
  const [handle, setHandle] = useState('')
  const [message, setMessage] = useState('')
  const [redirectUrl, setRedirectUrl] = useState('')
  const [redirectDelaySeconds, setRedirectDelaySeconds] = useState(0)
  const [handleError, setHandleError] = useState<string | null>(null)
  const [messageError, setMessageError] = useState<string | null>(null)
  const [handleAvailability, setHandleAvailability] = useState<'checking' | 'available' | 'taken' | 'error' | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)

  // Image upload
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageError, setImageError] = useState<string | null>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)

  // Step 2: account auth
  const [authMode, setAuthMode] = useState<AuthMode>('signup')
  const [authSubStep, setAuthSubStep] = useState<AuthSubStep>('creds')
  const [authEmail, setAuthEmail] = useState('')
  const [authPassword, setAuthPassword] = useState('')
  const [authConfirmPassword, setAuthConfirmPassword] = useState('')
  const [authCode, setAuthCode] = useState('')
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authInfo, setAuthInfo] = useState<string | null>(null)
  const [appearance, setAppearance] = useState<CodeAppearance>(defaultAppearance)

  useEffect(() => {
    const draft = localStorage.getItem('yowo:create-draft')
    if (!draft) return
    try {
      const parsed = JSON.parse(draft) as { handle?: string; message?: string; redirectUrl?: string; redirectDelaySeconds?: number; appearance?: CodeAppearance }
      if (parsed.handle) setHandle(parsed.handle)
      if (parsed.redirectDelaySeconds !== undefined) setRedirectDelaySeconds(parsed.redirectDelaySeconds)
      if (parsed.redirectUrl) setRedirectUrl(parsed.redirectUrl)
      if (parsed.message) setMessage(parsed.message)
      if (parsed.appearance) setAppearance(parsed.appearance)
    } catch {
      localStorage.removeItem('yowo:create-draft')
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('yowo:create-draft', JSON.stringify({ handle, message, redirectUrl, redirectDelaySeconds, appearance }))
  }, [handle, message, redirectUrl, redirectDelaySeconds, appearance])

  // Check handle availability with debouncing
  useEffect(() => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      if (handle && handle.length >= 3) {
        checkHandleAvailability(handle, controller.signal)
      } else {
        setHandleAvailability(null)
      }
    }, 500)
    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [handle])

  const validateForm = (): boolean => {
    let valid = !redirectUrlError(redirectUrl) && (!redirectUrl.trim() || !redirectDelayError(redirectDelaySeconds))
    if (!handle) {
      setHandleError('Code URL is required')
      valid = false
    } else if (handle.length < 3) {
      setHandleError('Must be at least 3 characters')
      valid = false
    } else if (handle.length > 32) {
      setHandleError('Must be 32 characters or less')
      valid = false
    } else if (!/^[a-z0-9-]+$/.test(handle)) {
      setHandleError('Lowercase letters, numbers, and hyphens only')
      valid = false
    } else {
      setHandleError(null)
    }
    if (!message && !imageFile && !redirectUrl.trim()) {
      setMessageError('A message, image, or redirect URL is required')
      valid = false
    } else if (message.length > 500) {
      setMessageError('Message must be 500 characters or less')
      valid = false
    } else {
      setMessageError(null)
    }
    return valid
  }

  const checkHandleAvailability = async (h: string, signal?: AbortSignal) => {
    setHandleAvailability('checking')
    try {
      const response = await fetch(buildApiUrl(`/messages/${h}`), { method: 'GET', signal })
      if (response.status === 404) {
        setHandleAvailability('available')
      } else if (response.ok) {
        setHandleAvailability('taken')
      } else {
        setHandleAvailability('error')
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setHandleAvailability('error')
    }
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setImageError('Only image files are allowed')
      return
    }
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setImageError('Use a JPEG, PNG, WebP, or GIF image')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image must be 5 MB or smaller')
      return
    }
    setImageError(null)
    setImageFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setImagePreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const clearImage = () => {
    setImageFile(null)
    setImagePreview(null)
    setImageError(null)
    if (imageInputRef.current) imageInputRef.current.value = ''
  }

  const captureImage = async () => {
    try {
      const dataUrl = await takeProfilePhoto()
      const blob = await (await fetch(dataUrl)).blob()
      const file = new File([blob], `yowo-photo.${blob.type.split('/')[1] || 'jpg'}`, { type: blob.type })
      setImageError(null)
      setImageFile(file)
      setImagePreview(dataUrl)
    } catch (error) {
      setImageError(error instanceof Error ? error.message : 'Could not open the camera')
    }
  }

  // Submit the code creation (called after auth is confirmed)
  const submitCode = async () => {
    setLoading(true)
    setSubmitError(null)
    try {
      const normalizedHandle = normalizeHandle(handle)

      let imageUrl: string | undefined
      if (imageFile) {
        setUploadingImage(true)
        try {
          const uploadRes = await fetch(buildApiUrl(`/upload-url/${normalizedHandle}`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getAccessToken()}` },
            body: JSON.stringify({ contentType: imageFile.type, fileSize: imageFile.size }),
          })
          if (!uploadRes.ok) throw new Error('Failed to get upload URL')
          const { uploadUrl, imageUrl: deliveredImageUrl } = await uploadRes.json()
          const s3Res = await fetch(uploadUrl, { method: 'PUT', body: imageFile, headers: { 'Content-Type': imageFile.type } })
          if (!s3Res.ok) throw new Error('Image upload failed')
          imageUrl = deliveredImageUrl
        } finally {
          setUploadingImage(false)
        }
      }

      const accessToken = await getAccessToken()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

      const response = await fetch(buildApiUrl(`/messages/${normalizedHandle}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ message, imageUrl, redirectUrl: redirectUrl.trim(), redirectDelaySeconds: redirectUrl.trim() ? redirectDelaySeconds : 0, appearance }),
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 409) {
          // Handle was taken while signing up — send back to step 1 to pick a new one
          setHandleAvailability('taken')
          setSubmitError('That code URL was just taken by someone else. Please choose a different one.')
          setStep(1)
          return
        }
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create code')
      }

      localStorage.removeItem('yowo:create-draft')
      navigate('/my-codes')
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An error occurred')
      setStep(1)
    } finally {
      setLoading(false)
    }
  }

  // Step 1 form submit
  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    if (isAccountLoggedIn()) {
      await submitCode()
    } else {
      setStep(2)
    }
  }

  // Back from step 2 — re-check availability in case handle was taken during auth
  const handleBack = () => {
    setStep(1)
    setAuthError(null)
    setHandleAvailability(null)
    if (handle && handle.length >= 3) {
      checkHandleAvailability(handle)
    }
  }

  // Step 2: signup
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError(null)
    if (authPassword !== authConfirmPassword) {
      setAuthError('Passwords do not match')
      return
    }
    if (authPassword.length < 8) {
      setAuthError('Password must be at least 8 characters')
      return
    }
    setAuthLoading(true)
    try {
      await accountSignUp(authEmail, authPassword)
      setAuthSubStep('verify')
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Sign up failed')
    } finally {
      setAuthLoading(false)
    }
  }

  // Step 2: verify email
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError(null)
    setAuthLoading(true)
    try {
      await accountConfirmSignUp(authEmail, authCode)
      // Sign in after verification so we have a token
      await accountSignIn(authEmail, authPassword)
      await submitCode()
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setAuthLoading(false)
    }
  }

  const handleResend = async () => {
    setAuthError(null)
    setAuthInfo(null)
    setAuthLoading(true)
    try {
      await accountResendConfirmationCode(authEmail)
      setAuthInfo('A new verification code was sent.')
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Could not resend the code')
    } finally {
      setAuthLoading(false)
    }
  }

  // Step 2: login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError(null)
    setAuthLoading(true)
    try {
      await accountSignIn(authEmail, authPassword)
      await submitCode()
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setAuthLoading(false)
    }
  }

  const getHandleStatus = () => {
    if (handleAvailability === 'checking') return <Badge variant="default">Checking...</Badge>
    if (handleAvailability === 'available') return <Badge variant="success">Available</Badge>
    if (handleAvailability === 'taken') return <Badge variant="danger">Taken</Badge>
    if (handleAvailability === 'error') return <Badge variant="danger">Couldn’t check</Badge>
    return null
  }

  const canSubmit = () =>
    handle &&
    (message || imageFile || redirectUrl.trim()) &&
    !redirectUrlError(redirectUrl) &&
    (!redirectUrl.trim() || !redirectDelayError(redirectDelaySeconds)) &&
    handleAvailability === 'available' &&
    !handleError &&
    !messageError

  const stepLabels = ['Your Code', 'Your Account']

  return (
    <>
      <Helmet>
        <title>Create Your Code - YouWont</title>
        <meta name="description" content="Create your personalized QR code with a custom message." />
      </Helmet>

      <div className="min-h-screen py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              Create Your Code
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {step === 1 ? 'Pick a name. Drop a message. Watch the curiosity unfold.' : 'One more step. Faster than making a sandwich, we promise.'}
            </p>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-center mb-8 gap-4">
            {stepLabels.map((label, i) => {
              const s = i + 1
              return (
                <div key={s} className="flex items-center">
                  <div className={`flex items-center gap-2`}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step >= s
                          ? 'bg-brand text-white'
                          : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                      }`}
                    >
                      {s}
                    </div>
                    <span className={`text-sm font-medium hidden sm:block ${step >= s ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'}`}>
                      {label}
                    </span>
                  </div>
                  {s < stepLabels.length && (
                    <div className={`w-12 h-0.5 mx-3 ${step > s ? 'bg-brand' : 'bg-slate-200 dark:bg-slate-700'}`} />
                  )}
                </div>
              )
            })}
          </div>

          {/* Step 1: Code form */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-6">
              <Card className="animate-fade-in">
                <div className="space-y-6">
                  <Input
                    label="Code URL"
                    hint="Choose a short name for your QR code. To open a website, paste its link in Redirect URL below."
                    placeholder="your-code"
                    value={handle}
                    onChange={(e) => setHandle(normalizeHandle(e.target.value))}
                    error={handleError ?? undefined}
                    required
                  />

                  <div className="flex items-center justify-between -mt-3">
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">
                      youwont.xyz/u/
                      <span className={handle ? 'text-brand dark:text-blue-400 font-semibold' : 'opacity-40'}>
                        {handle || 'your-code'}
                      </span>
                    </p>
                    {handle && getHandleStatus()}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Message
                    </label>
                    <textarea
                      className="w-full rounded-xl border bg-white px-3 py-2 text-slate-900 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-brand/40 focus:border-brand transition-colors duration-200 border-slate-300 dark:border-slate-700"
                      placeholder="What do you want to say?"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                    />
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Make it mysterious, funny, or just really, really weird. {message.length}/500
                    </span>
                    {messageError && (
                      <span className="text-xs text-red-600 dark:text-red-400">{messageError}</span>
                    )}
                  </div>

                  <Input
                    label="Redirect URL (optional)"
                    type="url"
                    placeholder="https://example.com"
                    value={redirectUrl}
                    onChange={(event) => setRedirectUrl(event.target.value)}
                    error={redirectUrlError(redirectUrl)}
                    maxLength={2048}
                    hint="Scans open this website. Add a delay below to show your message first, or clear this link to turn redirects off."
                  />

                  {redirectUrl.trim() && (
                    <Input
                      label="Redirect delay (seconds)"
                      type="number"
                      min={0}
                      max={3600}
                      step={1}
                      required
                      value={Number.isNaN(redirectDelaySeconds) ? '' : redirectDelaySeconds}
                      onChange={(event) => setRedirectDelaySeconds(event.target.valueAsNumber)}
                      error={redirectDelayError(redirectDelaySeconds)}
                      hint="0 opens the website immediately. Enter 1–3600 seconds to show your message and a countdown first."
                    />
                  )}

                  {/* Image upload */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Image <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    {imagePreview ? (
                      <div className="relative inline-block">
                        <img src={imagePreview} alt="Preview" className="max-h-40 rounded-xl border border-slate-200 dark:border-slate-700 object-cover" />
                        <button
                          type="button"
                          onClick={clearImage}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-3 w-full px-4 py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl cursor-pointer hover:border-brand dark:hover:border-brand hover:bg-brand/5 transition-colors">
                        <ImageIcon size={18} className="text-slate-400 shrink-0" />
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          Click to add an image to your code
                        </span>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageSelect}
                        />
                      </label>
                    )}
                    {isNativeApp() && !imagePreview && (
                      <Button type="button" variant="secondary" size="sm" onClick={() => void captureImage()}>
                        Take or choose a photo
                      </Button>
                    )}
                    {imageError && <p className="text-xs text-red-500">{imageError}</p>}
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Start with a template</label>
                    <div className="flex flex-wrap gap-2">
                      {messageTemplates.map((template) => (
                        <Button key={template.name} type="button" variant="secondary" size="sm" onClick={() => setMessage(template.message)}>
                          {template.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Public page theme
                      <select value={appearance.theme} onChange={(e) => setAppearance((current) => ({ ...current, theme: e.target.value as CodeAppearance['theme'] }))}
                        className="mt-1.5 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5">
                        <option value="clean">Clean</option><option value="midnight">Midnight</option><option value="sunset">Sunset</option><option value="forest">Forest</option>
                      </select>
                    </label>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Accent color
                      <input type="color" value={appearance.accentColor} onChange={(e) => setAppearance((current) => ({ ...current, accentColor: e.target.value }))} className="mt-1.5 block w-full h-11 rounded-xl" />
                    </label>
                  </div>

                  {submitError && <Alert variant="danger">{submitError}</Alert>}

                  {(message || imagePreview) && (
                    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center" style={{ background: themeStyles[appearance.theme].background, color: themeStyles[appearance.theme].foreground }} aria-label="Public page preview">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Preview</p>
                      {imagePreview && <img src={imagePreview} alt="Selected upload preview" className="max-h-40 mx-auto rounded-xl mb-3 object-contain" />}
                      {message && <p className="text-xl font-bold whitespace-pre-wrap break-words">{message}</p>}
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!canSubmit() || loading}
                    className="w-full"
                  >
                    {loading ? (uploadingImage ? 'Uploading image...' : 'Creating...') : 'Continue'}
                  </Button>
                </div>
              </Card>
            </form>
          )}

          {/* Step 2: Account */}
          {step === 2 && (
            <Card className="brand-panel animate-fade-in">
              {/* Mode toggle */}
              <div className="flex rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 mb-6">
                <button
                  type="button"
                  onClick={() => { setAuthMode('signup'); setAuthSubStep('creds'); setAuthError(null) }}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                    authMode === 'signup'
                      ? 'bg-brand text-white'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  Create Account
                </button>
                <button
                  type="button"
                  onClick={() => { setAuthMode('login'); setAuthSubStep('creds'); setAuthError(null) }}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                    authMode === 'login'
                      ? 'bg-brand text-white'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  Log In
                </button>
              </div>

              {/* Signup: creds step */}
              {authMode === 'signup' && authSubStep === 'creds' && (
                <form onSubmit={handleSignup} className="space-y-4">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="you@example.com"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    required
                  />
                  <Input
                    label="Password"
                    type="password"
                    placeholder="Min 8 chars, upper, lower, number, symbol"
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    hint="Min 8 characters with uppercase, lowercase, number, and symbol"
                    required
                  />
                  <Input
                    label="Confirm Password"
                    type="password"
                    placeholder="Repeat password"
                    value={authConfirmPassword}
                    onChange={(e) => setAuthConfirmPassword(e.target.value)}
                    required
                  />
                  {authError && <Alert variant="danger">{authError}</Alert>}
                  {authInfo && <Alert variant="success">{authInfo}</Alert>}
                  <Button type="submit" variant="primary" disabled={authLoading} className="w-full">
                    {authLoading ? 'Creating Account...' : 'Create Account & Continue'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={handleResend} disabled={authLoading} className="w-full">
                    Resend verification code
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleBack} className="w-full">
                    ← Back
                  </Button>
                </form>
              )}

              {/* Signup: verify step */}
              {authMode === 'signup' && authSubStep === 'verify' && (
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-brand/5 dark:bg-brand/10 border border-brand/15 dark:border-brand/20">
                    <Mail size={18} className="text-brand dark:text-blue-400 flex-shrink-0" />
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      We sent a 6-digit code to <strong>{authEmail}</strong>
                    </p>
                  </div>
                  <Input
                    label="Verification Code"
                    type="text"
                    placeholder="123456"
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                  />
                  {authError && <Alert variant="danger">{authError}</Alert>}
                  <Button type="submit" variant="primary" disabled={authLoading || authCode.length < 6} className="w-full">
                    {authLoading ? 'Verifying...' : 'Verify & Create Code'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => setAuthSubStep('creds')} className="w-full">
                    ← Back
                  </Button>
                </form>
              )}

              {/* Login */}
              {authMode === 'login' && (
                <form onSubmit={handleLogin} className="space-y-4">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="you@example.com"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    required
                  />
                  <Input
                    label="Password"
                    type="password"
                    placeholder="Your password"
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    required
                  />
                  {authError && <Alert variant="danger">{authError}</Alert>}
                  <Button type="submit" variant="primary" disabled={authLoading} className="w-full">
                    {authLoading ? 'Logging In...' : 'Log In & Create Code'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleBack} className="w-full">
                    ← Back
                  </Button>
                </form>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  )
}

export default SetupNew
